import { useResidentContext } from "app/features/residents/provider/ResidentProvider";
import { useGetCurrentMedicationsQuery } from "app/services/api/medicationsApi";
import { RootState } from "app/services/store";
import { selectResidentActiveMedications } from "app/store/slices/medications";
import { selectIsOnline } from "app/store/slices/network";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import usePagination from "ui/molecules/pagination/usePagination";
import { useMedicationFrequencyFilter } from "app/features/medications/hooks/useMedicationFrequencyFilter";

export function useMedicationListingWithOffline(isPaginated: boolean = true) {
  const { residentId } = useResidentContext();
  const { currentPage, handlePageChange } = usePagination();
  const { frequencies, handleFrequencyChange, loadFrequencyFilters } =
    useMedicationFrequencyFilter();

  const isOnline = useSelector(selectIsOnline);
  const offlineData = useSelector((state: RootState) =>
    selectResidentActiveMedications(state, residentId),
  );

  const {
    data: onlineData,
    isFetching,
    isLoading,
    isError,
  } = useGetCurrentMedicationsQuery(
    {
      filter: {
        resident_id: residentId,
        shift: frequencies.length > 0 ? frequencies : undefined,
      },
      page: isPaginated ? currentPage : undefined,
      limit: !isPaginated ? -1 : undefined,
    },
    { skip: !residentId },
  );

  const data = useMemo(() => {
    if (isFetching || (isOnline && onlineData))
      return Array.isArray(onlineData) ? onlineData : onlineData?.data;
    if (isError && offlineData) return offlineData;
    return Array.isArray(onlineData) ? onlineData : onlineData?.data;
  }, [onlineData, offlineData, isOnline, isFetching, isError]);

  const metaData = useMemo(() => {
    if (onlineData && !Array.isArray(onlineData)) {
      return onlineData.meta;
    }
    return undefined;
  }, [onlineData]);

  return {
    data,
    isFetching,
    isLoading,
    meta: metaData,
    handlePageChange,
    frequencies,
    handleFrequencyChange,
    loadFrequencyFilters,
  };
}
